import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Input, InputNumber, Slider, Row, Col, Tag, Space, Alert, Rate, Table, Spin, Tabs, Modal, message, Tooltip, Progress, Empty, Divider } from 'antd';
import { ExperimentOutlined, PlusOutlined, DeleteOutlined, SendOutlined, ReloadOutlined, HistoryOutlined, CheckCircleOutlined, WarningOutlined, InfoCircleOutlined, StarOutlined, HeartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { patientService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import './AIDiagnosis.css';

const { TextArea } = Input;
const { Option } = Select;

function AIDiagnosis() {
    const { t } = useTranslation();
    const { user } = useAuth();
    // AI is gated per-doctor by the super admin.
    const aiEnabled = Boolean(user?.aiEnabled);

    // Form state
    const [symptoms, setSymptoms] = useState([{ name: '', duration: 'acute', severity: 5 }]);
    const [medicalHistory, setMedicalHistory] = useState('');
    const [currentMedications, setCurrentMedications] = useState('');
    const [allergies, setAllergies] = useState('');
    const [age, setAge] = useState(null);
    const [gender, setGender] = useState('');
    const [vitals, setVitals] = useState({
        temperature: '',
        bloodPressure: '',
        heartRate: '',
        respiratoryRate: '',
        oxygenSaturation: ''
    });
    const [patientId, setPatientId] = useState('');
    const [patients, setPatients] = useState([]);

    // State management
    const [loading, setLoading] = useState(false);
    const [diagnosisResult, setDiagnosisResult] = useState(null);
    const [activeTab, setActiveTab] = useState('input');
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [feedback, setFeedback] = useState({ accuracy: null, wasHelpful: null, notes: '' });
    const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
    const [viewModalVisible, setViewModalVisible] = useState(false);

    useEffect(() => {
        if (!aiEnabled) return; // Don't hit AI endpoints when access is disabled.
        loadPatients();
        fetchDiagnosisHistory();
    }, [aiEnabled]);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    // Auto-fill form when patient is selected
    const handlePatientSelect = async (selectedPatientId) => {
        setPatientId(selectedPatientId);

        if (!selectedPatientId) {
            // Clear all fields if patient is deselected
            setAge(null);
            setGender('');
            setAllergies('');
            setCurrentMedications('');
            setMedicalHistory('');
            return;
        }

        try {
            // Fetch patient details
            const patientResponse = await patientService.getPatient(selectedPatientId);
            const patient = patientResponse.data;

            // Calculate age from dateOfBirth
            if (patient.dateOfBirth) {
                const birthDate = new Date(patient.dateOfBirth);
                const today = new Date();
                let calculatedAge = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    calculatedAge--;
                }
                setAge(calculatedAge);
            }

            // Set gender if available
            if (patient.gender) {
                setGender(patient.gender);
            }

            // Set allergies
            if (patient.allergies && patient.allergies.length > 0) {
                setAllergies(patient.allergies.join(', '));
            }

            // Build medical history from chronic diseases
            let medHistory = '';
            if (patient.chronicDiseases && patient.chronicDiseases.length > 0) {
                medHistory = patient.chronicDiseases.join(', ');
            }

            // Fetch patient visits to extract medications and recent diagnoses
            const visitsResponse = await patientService.getVisits(selectedPatientId);
            const visits = visitsResponse.data || [];

            // Extract recent medications from last 3-5 visits
            const medicationSet = new Set();
            visits.slice(0, 5).forEach(visit => {
                if (visit.prescriptions && visit.prescriptions.length > 0) {
                    visit.prescriptions.forEach(prescription => {
                        if (prescription.drugName) {
                            medicationSet.add(prescription.drugName);
                        }
                    });
                }
            });
            setCurrentMedications(Array.from(medicationSet).join(', '));

            // Extract recent diagnoses to add to medical history
            const recentDiagnoses = visits
                .filter(v => v.diagnosis)
                .slice(0, 3)
                .map(v => v.diagnosis);

            if (recentDiagnoses.length > 0) {
                const diagnosisText = 'Recent diagnoses: ' + recentDiagnoses.join(', ');
                medHistory = medHistory ? medHistory + '; ' + diagnosisText : diagnosisText;
            }

            setMedicalHistory(medHistory);

            message.success(t('patientInfoLoaded') || 'Patient information loaded successfully');
        } catch (error) {
            console.error('Error loading patient details:', error);
            message.error(t('failedToLoadPatientInfo') || 'Failed to load patient information');
        }
    };

    const fetchDiagnosisHistory = async () => {
        setHistoryLoading(true);
        try {
            const response = await api.get('/diagnosis/history');
            console.log('Diagnosis history response:', response.data);
            if (response.data.success) {
                const historyData = response.data.data || [];
                console.log('Setting history:', historyData);
                setHistory(historyData);
                if (historyData.length === 0) {
                    message.info(t('noHistory') || 'No diagnosis history found');
                }
            }
        } catch (err) {
            console.error('Failed to fetch history:', err);
            message.error(err.response?.data?.message || t('failedToFetchDiagnosisHistory') || 'Failed to fetch diagnosis history');
        } finally {
            setHistoryLoading(false);
        }
    };

    // Symptoms management
    const addSymptom = () => {
        setSymptoms([...symptoms, { name: '', duration: 'acute', severity: 5 }]);
    };

    const removeSymptom = (index) => {
        if (symptoms.length > 1) {
            setSymptoms(symptoms.filter((_, i) => i !== index));
        }
    };

    const updateSymptom = (index, field, value) => {
        const newSymptoms = [...symptoms];
        newSymptoms[index][field] = value;
        setSymptoms(newSymptoms);
    };

    // Validate + submit
    const handleGetDiagnosis = async () => {
        if (symptoms.some(s => !s.name.trim())) {
            message.error(t('pleaseFillSymptoms') || 'Please fill in all symptom names');
            return;
        }

        setLoading(true);
        try {
            const medicationsArray = currentMedications
                .split(',')
                .map(m => m.trim())
                .filter(m => m);
            const allergiesArray = allergies
                .split(',')
                .map(a => a.trim())
                .filter(a => a);

            const payload = {
                symptoms,
                medicalHistory,
                currentMedications: medicationsArray,
                allergies: allergiesArray,
                age: age || undefined,
                gender,
                vitals: Object.fromEntries(
                    Object.entries(vitals)
                        .filter(([_, v]) => v !== '')
                        .map(([k, v]) => [k, k === 'bloodPressure' ? v : Number(v)])
                ),
                patientId: patientId || undefined
            };

            const response = await api.post('/diagnosis/get-diagnosis', payload);

            if (response.data.success) {
                setDiagnosisResult(response.data.data);
                setActiveTab('results');
                message.success(t('diagnosisComplete') || 'Diagnosis analysis completed successfully!');
                fetchDiagnosisHistory();
            }
        } catch (err) {
            message.error(err.response?.data?.message || t('diagnosisFailed') || 'Failed to get diagnosis.');
        } finally {
            setLoading(false);
        }
    };

    // Submit feedback
    const handleSubmitFeedback = async () => {
        if (!diagnosisResult) return;

        try {
            const response = await api.put(
                `/diagnosis/${diagnosisResult.diagnosisId}/feedback`,
                {
                    accuracy: feedback.accuracy,
                    wasHelpful: feedback.wasHelpful,
                    notes: feedback.notes
                }
            );

            if (response.data.success) {
                message.success(t('feedbackSubmitted') || 'Feedback submitted successfully!');
                setFeedbackModalVisible(false);
            }
        } catch (err) {
            message.error(t('feedbackFailed') || 'Failed to submit feedback');
        }
    };

    // Reset form
    const handleReset = () => {
        setSymptoms([{ name: '', duration: 'acute', severity: 5 }]);
        setMedicalHistory('');
        setCurrentMedications('');
        setAllergies('');
        setAge(null);
        setGender('');
        setVitals({
            temperature: '',
            bloodPressure: '',
            heartRate: '',
            respiratoryRate: '',
            oxygenSaturation: ''
        });
        setPatientId('');
        setDiagnosisResult(null);
        setFeedback({ accuracy: null, wasHelpful: null, notes: '' });
        setActiveTab('input');
    };

    // Severity color
    const getSeverityColor = (severity) => {
        if (severity <= 3) return '#02C39A';
        if (severity <= 6) return '#faad14';
        return '#ff4d4f';
    };

    // History columns
    const historyColumns = [
        {
            title: t('symptoms') || 'Symptoms',
            dataIndex: 'symptoms',
            key: 'symptoms',
            width: 300,
            render: (symptoms) => (
                <Space wrap>
                    {symptoms && symptoms.length > 0 ? (
                        symptoms.map((s, i) => (
                            <Tag key={i} color="blue">{s.name}</Tag>
                        ))
                    ) : (
                        <span>-</span>
                    )}
                </Space>
            )
        },
        {
            title: t('topDiagnosis') || 'Top Diagnosis',
            dataIndex: 'aiResponse',
            key: 'diagnosis',
            width: 250,
            render: (aiResponse) => {
                if (aiResponse && aiResponse.possibleDiagnoses && aiResponse.possibleDiagnoses.length > 0) {
                    const topDiagnosis = aiResponse.possibleDiagnoses[0];
                    return (
                        <Tag color="purple">
                            {topDiagnosis.condition} ({topDiagnosis.probability}%)
                        </Tag>
                    );
                }
                return <span>-</span>;
            }
        },
        {
            title: t('date') || 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
            sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                        setSelectedHistoryItem(record);
                        setViewModalVisible(true);
                    }}
                >
                    {t('view') || 'View'}
                </Button>
            )
        }
    ];

    // Tab items
    const tabItems = [
        {
            key: 'input',
            label: (
                <span>
                    <ExperimentOutlined /> {t('newDiagnosis') || 'New Diagnosis'}
                </span>
            ),
            children: (
                <div className="ai-diagnosis-form">
                    {/* Patient & Demographics */}
                    <Card title={t('patientInfo') || 'Patient Information'} className="form-section-card" size="small">
                        <Row gutter={16}>
                            <Col xs={24} sm={8}>
                                <div className="form-field">
                                    <label>{t('patient') || 'Patient'} ({t('optional') || 'Optional'})</label>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder={t('selectPatient') || 'Select patient'}
                                        value={patientId || undefined}
                                        onChange={handlePatientSelect}
                                        optionFilterProp="children"
                                        style={{ width: '100%' }}
                                    >
                                        {patients.map(p => (
                                            <Option key={p._id} value={p._id}>{p.name}</Option>
                                        ))}
                                    </Select>
                                </div>
                            </Col>
                            <Col xs={24} sm={8}>
                                <div className="form-field">
                                    <label>{t('age') || 'Age'}</label>
                                    <InputNumber
                                        min={0}
                                        max={150}
                                        value={age}
                                        onChange={setAge}
                                        placeholder={t('enterAge') || 'Enter age'}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={8}>
                                <div className="form-field">
                                    <label>{t('gender') || 'Gender'}</label>
                                    <Select
                                        value={gender || undefined}
                                        onChange={setGender}
                                        placeholder={t('selectGender') || 'Select gender'}
                                        style={{ width: '100%' }}
                                        allowClear
                                    >
                                        <Option value="male">{t('male') || 'Male'}</Option>
                                        <Option value="female">{t('female') || 'Female'}</Option>
                                        <Option value="other">{t('other') || 'Other'}</Option>
                                    </Select>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Vitals */}
                    <Card title={<><HeartOutlined /> {t('vitals') || 'Vitals'} ({t('optional') || 'Optional'})</>} className="form-section-card" size="small">
                        <Row gutter={16}>
                            <Col xs={24} sm={8} md={5}>
                                <div className="form-field">
                                    <label>{t('temperature') || 'Temp'} (°C)</label>
                                    <Input
                                        type="number"
                                        value={vitals.temperature}
                                        onChange={(e) => setVitals({ ...vitals, temperature: e.target.value })}
                                        placeholder="36.5"
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={8} md={5}>
                                <div className="form-field">
                                    <label>{t('bloodPressure') || 'Blood Pressure'}</label>
                                    <Input
                                        value={vitals.bloodPressure}
                                        onChange={(e) => setVitals({ ...vitals, bloodPressure: e.target.value })}
                                        placeholder="120/80"
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={8} md={5}>
                                <div className="form-field">
                                    <label>{t('heartRate') || 'Heart Rate'} (bpm)</label>
                                    <Input
                                        type="number"
                                        value={vitals.heartRate}
                                        onChange={(e) => setVitals({ ...vitals, heartRate: e.target.value })}
                                        placeholder="72"
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={8} md={5}>
                                <div className="form-field">
                                    <label>{t('respiratoryRate') || 'Resp. Rate'}</label>
                                    <Input
                                        type="number"
                                        value={vitals.respiratoryRate}
                                        onChange={(e) => setVitals({ ...vitals, respiratoryRate: e.target.value })}
                                        placeholder="16"
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={8} md={4}>
                                <div className="form-field">
                                    <label>O₂ Sat (%)</label>
                                    <Input
                                        type="number"
                                        value={vitals.oxygenSaturation}
                                        onChange={(e) => setVitals({ ...vitals, oxygenSaturation: e.target.value })}
                                        placeholder="98"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Symptoms */}
                    <Card
                        title={<><WarningOutlined /> {t('symptoms') || 'Symptoms'} *</>}
                        className="form-section-card"
                        size="small"
                        extra={
                            <Button type="primary" icon={<PlusOutlined />} onClick={addSymptom} size="small">
                                {t('addSymptom') || 'Add Symptom'}
                            </Button>
                        }
                    >
                        {symptoms.map((symptom, index) => (
                            <div key={index} className="symptom-row">
                                <Row gutter={12} align="middle">
                                    <Col xs={24} sm={8}>
                                        <Input
                                            value={symptom.name}
                                            onChange={(e) => updateSymptom(index, 'name', e.target.value)}
                                            placeholder={t('symptomName') || 'e.g., Headache, Fever'}
                                        />
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Select
                                            value={symptom.duration}
                                            onChange={(val) => updateSymptom(index, 'duration', val)}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="acute">{t('acute') || 'Acute'}</Option>
                                            <Option value="chronic">{t('chronic') || 'Chronic'}</Option>
                                            <Option value="intermittent">{t('intermittent') || 'Intermittent'}</Option>
                                        </Select>
                                    </Col>
                                    <Col xs={20} sm={8}>
                                        <div className="severity-slider">
                                            <span style={{ fontSize: 12, color: getSeverityColor(symptom.severity) }}>
                                                {t('severity') || 'Severity'}: {symptom.severity}/10
                                            </span>
                                            <Slider
                                                min={1}
                                                max={10}
                                                value={symptom.severity}
                                                onChange={(val) => updateSymptom(index, 'severity', val)}
                                                trackStyle={{ backgroundColor: getSeverityColor(symptom.severity) }}
                                                handleStyle={{ borderColor: getSeverityColor(symptom.severity) }}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={4} sm={2} style={{ textAlign: 'center' }}>
                                        <Button
                                            danger
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            disabled={symptoms.length === 1}
                                            onClick={() => removeSymptom(index)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </Card>

                    {/* Medical History, Medications, Allergies */}
                    <Card title={<><InfoCircleOutlined /> {t('medicalBackground') || 'Medical Background'}</>} className="form-section-card" size="small">
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-field">
                                    <label>{t('medicalHistory') || 'Medical History'}</label>
                                    <TextArea
                                        value={medicalHistory}
                                        onChange={(e) => setMedicalHistory(e.target.value)}
                                        rows={3}
                                        placeholder={t('medicalHistoryPlaceholder') || 'e.g., Diabetes, Hypertension, Previous surgeries...'}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={12}>
                                <div className="form-field">
                                    <label>{t('currentMedications') || 'Current Medications'}</label>
                                    <TextArea
                                        value={currentMedications}
                                        onChange={(e) => setCurrentMedications(e.target.value)}
                                        rows={2}
                                        placeholder={t('medicationsPlaceholder') || 'Comma separated: Aspirin, Metformin, Lisinopril'}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={12}>
                                <div className="form-field">
                                    <label>{t('allergies') || 'Allergies'}</label>
                                    <TextArea
                                        value={allergies}
                                        onChange={(e) => setAllergies(e.target.value)}
                                        rows={2}
                                        placeholder={t('allergiesPlaceholder') || 'Comma separated: Penicillin, Sulfonamides, Latex'}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Action buttons */}
                    <div className="form-actions">
                        <Button onClick={handleReset} icon={<ReloadOutlined />}>
                            {t('reset') || 'Reset'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={loading}
                            onClick={handleGetDiagnosis}
                            size="large"
                        >
                            {loading ? (t('analyzing') || 'Analyzing...') : (t('getDiagnosis') || 'Get AI Diagnosis')}
                        </Button>
                    </div>
                </div>
            )
        },
        {
            key: 'results',
            label: (
                <span>
                    <CheckCircleOutlined /> {t('results') || 'Results'}
                </span>
            ),
            disabled: !diagnosisResult,
            children: diagnosisResult ? (
                <div className="diagnosis-results">
                    {/* Disclaimer */}
                    <Alert
                        message={t('aiDisclaimer') || 'AI Disclaimer'}
                        description={diagnosisResult.analysis.disclaimer}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    {/* Warnings */}
                    {diagnosisResult.analysis.warnings && diagnosisResult.analysis.warnings.length > 0 && (
                        <Alert
                            message={t('warningSigns') || 'Warning Signs'}
                            description={
                                <ul className="warning-list">
                                    {diagnosisResult.analysis.warnings.map((w, i) => (
                                        <li key={i}>{w}</li>
                                    ))}
                                </ul>
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Possible Diagnoses */}
                    <Card title={t('possibleDiagnoses') || 'Possible Diagnoses'} className="results-card">
                        {diagnosisResult.analysis.possibleDiagnoses.map((diag, index) => (
                            <div key={index} className="diagnosis-item">
                                <div className="diagnosis-header">
                                    <div>
                                        <h4 className="diagnosis-condition">{diag.condition}</h4>
                                        <p className="diagnosis-description">{diag.description}</p>
                                    </div>
                                    <div className="diagnosis-probability">
                                        <Progress
                                            type="circle"
                                            percent={diag.probability}
                                            width={60}
                                            strokeColor={diag.probability > 60 ? '#ff4d4f' : diag.probability > 30 ? '#faad14' : '#02C39A'}
                                        />
                                    </div>
                                </div>
                                {diag.matchingSymptoms && diag.matchingSymptoms.length > 0 && (
                                    <div className="diagnosis-symptoms">
                                        <span className="label">{t('matchingSymptoms') || 'Matching Symptoms'}:</span>
                                        <Space wrap>
                                            {diag.matchingSymptoms.map((s, i) => (
                                                <Tag key={i} color="blue">{s}</Tag>
                                            ))}
                                        </Space>
                                    </div>
                                )}
                                {diag.recommendations && diag.recommendations.length > 0 && (
                                    <div className="diagnosis-recommendations">
                                        <span className="label">{t('recommendations') || 'Recommendations'}:</span>
                                        <ul>
                                            {diag.recommendations.map((r, i) => (
                                                <li key={i}>{r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {index < diagnosisResult.analysis.possibleDiagnoses.length - 1 && <Divider />}
                            </div>
                        ))}
                    </Card>

                    {/* Recommended Tests & Follow-up */}
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        {diagnosisResult.analysis.recommendedTests && diagnosisResult.analysis.recommendedTests.length > 0 && (
                            <Col xs={24} sm={12}>
                                <Card title={t('recommendedTests') || 'Recommended Tests'} className="results-card" size="small">
                                    <ul className="results-list">
                                        {diagnosisResult.analysis.recommendedTests.map((test, i) => (
                                            <li key={i}><Tag color="cyan">{test}</Tag></li>
                                        ))}
                                    </ul>
                                </Card>
                            </Col>
                        )}
                        {diagnosisResult.analysis.followUpActions && diagnosisResult.analysis.followUpActions.length > 0 && (
                            <Col xs={24} sm={12}>
                                <Card title={t('followUpActions') || 'Follow-up Actions'} className="results-card" size="small">
                                    <ul className="results-list">
                                        {diagnosisResult.analysis.followUpActions.map((action, i) => (
                                            <li key={i}><Tag color="green">{action}</Tag></li>
                                        ))}
                                    </ul>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    {/* Action Buttons */}
                    <div className="results-actions">
                        <Button onClick={() => setActiveTab('input')} icon={<ReloadOutlined />}>
                            {t('newAnalysis') || 'New Analysis'}
                        </Button>
                        <Button type="primary" icon={<StarOutlined />} onClick={() => setFeedbackModalVisible(true)}>
                            {t('giveFeedback') || 'Give Feedback'}
                        </Button>
                    </div>
                </div>
            ) : null
        },
        {
            key: 'history',
            label: (
                <span>
                    <HistoryOutlined /> {t('history') || 'History'}
                </span>
            ),
            children: (
                <div className="diagnosis-history">
                    <Button
                        onClick={fetchDiagnosisHistory}
                        loading={historyLoading}
                        icon={<ReloadOutlined />}
                        style={{ marginBottom: 16 }}
                    >
                        {t('refresh') || 'Refresh'}
                    </Button>
                    {history.length === 0 ? (
                        <Empty description={t('noHistory') || 'No diagnosis history yet'} />
                    ) : (
                        <Table
                            dataSource={history}
                            columns={historyColumns}
                            rowKey="_id"
                            loading={historyLoading}
                            pagination={{ pageSize: 10 }}
                        />
                    )}
                </div>
            )
        }
    ];

    // AI features are off until the super admin activates them for this doctor.
    if (!aiEnabled) {
        return (
            <div className="ai-diagnosis-container" style={{ padding: 24 }}>
                <Alert
                    type="warning"
                    showIcon
                    message={t('aiFeatureLocked') || 'AI features are not enabled'}
                    description={t('aiFeatureLockedDesc') || 'AI features are disabled for your account. Please contact the administrator to enable them.'}
                />
            </div>
        );
    }

    return (
        <div className="ai-diagnosis-container">
            <div className="ai-diagnosis-header">
                <div className="header-title">
                    <ExperimentOutlined className="header-icon" />
                    <div>
                        <h1>{t('aiDiagnosis') || 'AI Diagnosis Assistant'}</h1>
                        <p>{t('poweredByGPT') || 'Powered by GPT-4'}</p>
                    </div>
                </div>
            </div>

            <Alert
                message={t('disclaimerTitle') || 'Clinical Decision Support Only'}
                description={t('disclaimerText') || 'This tool is NOT a substitute for professional medical diagnosis. Always consult with qualified healthcare providers.'}
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 20 }}
                closable
            />

            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    if (key === 'history') fetchDiagnosisHistory();
                }}
                items={tabItems}
                type="card"
                size="large"
            />

            {/* Feedback Modal */}
            <Modal
                title={t('diagnosisFeedback') || 'Diagnosis Feedback'}
                open={feedbackModalVisible}
                onOk={handleSubmitFeedback}
                onCancel={() => setFeedbackModalVisible(false)}
                okText={t('submitFeedback') || 'Submit Feedback'}
            >
                <div className="feedback-form">
                    <div className="form-field">
                        <label>{t('wasHelpful') || 'Was this diagnosis helpful?'}</label>
                        <Space>
                            <Button
                                type={feedback.wasHelpful === true ? 'primary' : 'default'}
                                onClick={() => setFeedback({ ...feedback, wasHelpful: true })}
                                style={feedback.wasHelpful === true ? { backgroundColor: '#02C39A', borderColor: '#02C39A' } : {}}
                            >
                                {t('yes') || 'Yes'}
                            </Button>
                            <Button
                                type={feedback.wasHelpful === false ? 'primary' : 'default'}
                                danger={feedback.wasHelpful === false}
                                onClick={() => setFeedback({ ...feedback, wasHelpful: false })}
                            >
                                {t('no') || 'No'}
                            </Button>
                        </Space>
                    </div>
                    <div className="form-field">
                        <label>{t('accuracyRating') || 'Accuracy Rating'}</label>
                        <Rate
                            value={feedback.accuracy}
                            onChange={(val) => setFeedback({ ...feedback, accuracy: val })}
                        />
                    </div>
                    <div className="form-field">
                        <label>{t('notes') || 'Additional Notes'}</label>
                        <TextArea
                            value={feedback.notes}
                            onChange={(e) => setFeedback({ ...feedback, notes: e.target.value })}
                            rows={3}
                            placeholder={t('feedbackPlaceholder') || 'Share your feedback...'}
                        />
                    </div>
                </div>
            </Modal>

            {/* View Diagnosis History Modal */}
            <Modal
                title={t('diagnosisDetails') || 'Diagnosis Details'}
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                width={900}
                footer={[
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        {t('close') || 'Close'}
                    </Button>
                ]}
            >
                {selectedHistoryItem && (
                    <div className="diagnosis-view-modal">
                        {/* Patient Information */}
                        {(selectedHistoryItem.age || selectedHistoryItem.gender) && (
                            <Card size="small" style={{ marginBottom: 16 }} title={t('patientInfo') || 'Patient Information'}>
                                <Row gutter={16}>
                                    {selectedHistoryItem.age && (
                                        <Col xs={12}>
                                            <strong>{t('age') || 'Age'}:</strong> {selectedHistoryItem.age}
                                        </Col>
                                    )}
                                    {selectedHistoryItem.gender && (
                                        <Col xs={12}>
                                            <strong>{t('gender') || 'Gender'}:</strong> {t(selectedHistoryItem.gender) || selectedHistoryItem.gender}
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        )}

                        {/* Symptoms */}
                        {selectedHistoryItem.symptoms && selectedHistoryItem.symptoms.length > 0 && (
                            <Card size="small" style={{ marginBottom: 16 }} title={t('symptoms') || 'Symptoms'}>
                                <div>
                                    {selectedHistoryItem.symptoms.map((symptom, idx) => (
                                        <div key={idx} style={{ marginBottom: 8 }}>
                                            <Tag color="blue">{symptom.name}</Tag>
                                            <span style={{ marginLeft: 8, fontSize: 12 }}>
                                                {t('severity') || 'Severity'}: {symptom.severity}/10 • {t(symptom.duration) || symptom.duration}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Medical History */}
                        {selectedHistoryItem.medicalHistory && (
                            <Card size="small" style={{ marginBottom: 16 }} title={t('medicalHistory') || 'Medical History'}>
                                <p>{selectedHistoryItem.medicalHistory}</p>
                            </Card>
                        )}

                        {/* Medications */}
                        {selectedHistoryItem.currentMedications && selectedHistoryItem.currentMedications.length > 0 && (
                            <Card size="small" style={{ marginBottom: 16 }} title={t('currentMedications') || 'Current Medications'}>
                                <div>
                                    {selectedHistoryItem.currentMedications.map((med, idx) => (
                                        <Tag key={idx} color="cyan" style={{ marginRight: 8, marginBottom: 8 }}>{med}</Tag>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Allergies */}
                        {selectedHistoryItem.allergies && selectedHistoryItem.allergies.length > 0 && (
                            <Card size="small" style={{ marginBottom: 16 }} title={t('allergies') || 'Allergies'}>
                                <div>
                                    {selectedHistoryItem.allergies.map((allergy, idx) => (
                                        <Tag key={idx} color="red" style={{ marginRight: 8, marginBottom: 8 }}>{allergy}</Tag>
                                    ))}
                                </div>
                            </Card>
                        )}

                        <Divider />

                        {/* AI Response */}
                        {selectedHistoryItem.aiResponse && (
                            <>
                                {/* Warnings */}
                                {selectedHistoryItem.aiResponse.warnings && selectedHistoryItem.aiResponse.warnings.length > 0 && (
                                    <Alert
                                        message={t('warningSigns') || 'Warning Signs'}
                                        description={
                                            <ul className="warning-list">
                                                {selectedHistoryItem.aiResponse.warnings.map((w, i) => (
                                                    <li key={i}>{w}</li>
                                                ))}
                                            </ul>
                                        }
                                        type="error"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                {/* Possible Diagnoses */}
                                {selectedHistoryItem.aiResponse.possibleDiagnoses && selectedHistoryItem.aiResponse.possibleDiagnoses.length > 0 && (
                                    <Card size="small" style={{ marginBottom: 16 }} title={t('possibleDiagnoses') || 'Possible Diagnoses'}>
                                        {selectedHistoryItem.aiResponse.possibleDiagnoses.map((diag, idx) => (
                                            <div key={idx} style={{ marginBottom: 16 }}>
                                                <Row gutter={16} align="middle">
                                                    <Col flex="auto">
                                                        <h4 style={{ margin: '0 0 8px 0' }}>{diag.condition}</h4>
                                                        <p style={{ margin: 0, fontSize: 12 }}>{diag.description}</p>
                                                    </Col>
                                                    <Col>
                                                        <Progress
                                                            type="circle"
                                                            percent={diag.probability}
                                                            width={60}
                                                            strokeColor={diag.probability > 60 ? '#ff4d4f' : diag.probability > 30 ? '#faad14' : '#02C39A'}
                                                        />
                                                    </Col>
                                                </Row>
                                                {diag.matchingSymptoms && diag.matchingSymptoms.length > 0 && (
                                                    <div style={{ marginTop: 8 }}>
                                                        <strong>{t('matchingSymptoms') || 'Matching Symptoms'}:</strong>
                                                        <Space wrap size="small" style={{ marginTop: 4 }}>
                                                            {diag.matchingSymptoms.map((s, i) => (
                                                                <Tag key={i} color="blue" style={{ fontSize: 11 }}>{s}</Tag>
                                                            ))}
                                                        </Space>
                                                    </div>
                                                )}
                                                {diag.recommendations && diag.recommendations.length > 0 && (
                                                    <div style={{ marginTop: 8 }}>
                                                        <strong>{t('recommendations') || 'Recommendations'}:</strong>
                                                        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                                                            {diag.recommendations.map((r, i) => (
                                                                <li key={i} style={{ fontSize: 12 }}>{r}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {idx < selectedHistoryItem.aiResponse.possibleDiagnoses.length - 1 && <Divider style={{ margin: '8px 0' }} />}
                                            </div>
                                        ))}
                                    </Card>
                                )}

                                {/* Recommended Tests */}
                                {selectedHistoryItem.aiResponse.recommendedTests && selectedHistoryItem.aiResponse.recommendedTests.length > 0 && (
                                    <Card size="small" style={{ marginBottom: 16 }} title={t('recommendedTests') || 'Recommended Tests'}>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {selectedHistoryItem.aiResponse.recommendedTests.map((test, i) => (
                                                <li key={i}>{test}</li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}

                                {/* Follow-up Actions */}
                                {selectedHistoryItem.aiResponse.followUpActions && selectedHistoryItem.aiResponse.followUpActions.length > 0 && (
                                    <Card size="small" style={{ marginBottom: 16 }} title={t('followUpActions') || 'Follow-up Actions'}>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {selectedHistoryItem.aiResponse.followUpActions.map((action, i) => (
                                                <li key={i}>{action}</li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}

                                {/* Disclaimer */}
                                {selectedHistoryItem.aiResponse.disclaimer && (
                                    <Alert
                                        message={t('aiDisclaimer') || 'AI Disclaimer'}
                                        description={selectedHistoryItem.aiResponse.disclaimer}
                                        type="warning"
                                        showIcon
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default AIDiagnosis;
